import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SearchEntityResult } from '../../../../../api/models/search-entity-result';
import { SearchGenericUrl } from '../../../../../api/models/search-generic-url';
import { SearchParams } from '../../../../../api/models/search-params';
import { SearchEventTypeEnum } from '../../../../../shared/model/search-event-type-enum';
import { SearchEventPublisher } from '../../../../../shared/service/search-event-publisher.service';

@Component({
  selector: 'app-search-entity-cta',
  templateUrl: './search-entity-cta.component.html',
  styleUrls: ['./search-entity-cta.component.scss']
})
export class SearchEntityCtaComponent implements OnInit, OnChanges {

  @Input()
  public searchResultEntry!: SearchEntityResult;

  @Input()
  public isShowContactUs: boolean = false;

  @Input()
  public isShowPurchaseUrl: boolean = false;

  @Input()
  public listPrice: string = 'N/A';

  @Input()
  public currencyCode: string = 'N/A';

  constructor(private searchEventPublisher: SearchEventPublisher, private translateService: TranslateService) {
  }

  primaryLink?: SearchGenericUrl;
  secondaryLinks: (SearchGenericUrl | undefined)[] = [];
  otherLinks: (SearchGenericUrl | undefined)[] = [];
  addToCart: boolean = false;
  showAddToCartError: boolean = false;

  ngOnInit(): void {
    this.initButtons();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.searchResultEntry || changes.isShowPurchaseUrl || changes.listPrice || changes.currencyCode) {
      this.initButtons();
    }
  }

  private initButtons() {
    if (this.searchResultEntry) {
      const { entityType } = this.searchResultEntry;

      if ('ModelConcept' === entityType) {
        this.initModelConceptLinks();
      }

      if ('ProductItem' === entityType) {
        this.initProductItemLinks();
      }

      if ('Product' === entityType) {
        this.initSoftwareProductLinks();
      }
    }
  }

  private initSoftwareProductLinks() {
    this.primaryLink = {
      displayString: this.translateService.instant('productDetails'),
      englishDisplayString: this.translateService.instant('productDetailsEn'),
      url: this.searchResultEntry.uri
    };

    this.secondaryLinks = [
      this.searchResultEntry.downloadLink,
      this.searchResultEntry.compareEditionsLink
    ];

    this.otherLinks = [
      this.searchResultEntry.roadmapLink,
      this.searchResultEntry.releaseNoteLink,
      this.searchResultEntry.compatibilityLink,
      this.searchResultEntry.userManualLink,
      this.searchResultEntry.referenceManualLink
    ];
  }

  private initModelConceptLinks() {
    if (this.searchResultEntry.webOrderable && this.searchResultEntry.distributorExperience && this.isShowPurchaseUrl) {
      this.addToCart = this.isShowPurchaseUrl;

      this.secondaryLinks = [
        {
          displayString: this.translateService.instant('productDetails'),
          englishDisplayString: this.translateService.instant('productDetailsEn'),
          url: this.searchResultEntry.uri
        },
        this.searchResultEntry.specificationLink,
        this.searchResultEntry.driverLink
      ];
    } else {
      this.primaryLink = {
        displayString: this.translateService.instant('productDetails'),
        englishDisplayString: this.translateService.instant('productDetailsEn'),
        url: this.searchResultEntry.uri
      };

      this.secondaryLinks = [
        this.searchResultEntry.specificationLink,
        this.searchResultEntry.driverLink
      ];
    }

    this.otherLinks = [
      this.searchResultEntry.gettingStartedLink,
      this.searchResultEntry.userManualLink,
      this.searchResultEntry.compatibilityLink,
      this.searchResultEntry.dimensionalDrawingLink,
      this.searchResultEntry.regulatoryLink,
      this.searchResultEntry.calibrationLink
    ];
  }

  private initProductItemLinks() {
    if (this.searchResultEntry.webSellable || this.searchResultEntry.webOrderable) {
      this.addToCart = this.isShowPurchaseUrl;
    }

    if ('Hardware' === this.searchResultEntry.productType) {
      if (this.searchResultEntry.webSellable || this.searchResultEntry.webOrderable) {
        this.secondaryLinks = [
          {
            displayString: this.translateService.instant('productDetails'),
            englishDisplayString: this.translateService.instant('productDetailsEn'),
            url: this.searchResultEntry.uri
          },
          this.searchResultEntry.specificationLink,
          this.searchResultEntry.driverLink
        ];
      } else {
        this.primaryLink = {
          displayString: this.translateService.instant('productDetails'),
          englishDisplayString: this.translateService.instant('productDetailsEn'),
          url: this.searchResultEntry.uri
        };

        this.secondaryLinks = [
          this.searchResultEntry.specificationLink,
          this.searchResultEntry.driverLink
        ];
      }

      this.otherLinks = [
        this.searchResultEntry.gettingStartedLink,
        this.searchResultEntry.userManualLink,
        this.searchResultEntry.compatibilityLink,
        this.searchResultEntry.dimensionalDrawingLink,
        this.searchResultEntry.regulatoryLink,
        this.searchResultEntry.calibrationLink
      ];
    }

    if ('Bundle' === this.searchResultEntry.productType) {
      if (this.searchResultEntry.webSellable || this.searchResultEntry.webOrderable) {
        this.secondaryLinks = [
          {
            displayString: this.translateService.instant('productDetails'),
            englishDisplayString: this.translateService.instant('productDetailsEn'),
            url: this.searchResultEntry.uri
          }
        ];
      } else {
        this.primaryLink = {
          displayString: this.translateService.instant('productDetails'),
          englishDisplayString: this.translateService.instant('productDetailsEn'),
          url: this.searchResultEntry.uri
        };
      }
    }

    if ('Software' === this.searchResultEntry.productType) {
      if (this.searchResultEntry.webSellable) {
        this.secondaryLinks = [
          {
            displayString: this.translateService.instant('productDetails'),
            englishDisplayString: this.translateService.instant('productDetailsEn'),
            url: this.searchResultEntry.uri
          },
          this.searchResultEntry.downloadLink
        ];
      }

      if (!this.searchResultEntry.webSellable) {
        this.primaryLink = {
          displayString: this.translateService.instant('productDetails'),
          englishDisplayString: this.translateService.instant('productDetailsEn'),
          url: this.searchResultEntry.uri
        };

        this.secondaryLinks = [this.searchResultEntry.downloadLink];
      }

      this.otherLinks = [
        this.searchResultEntry.releaseNoteLink,
        this.searchResultEntry.compatibilityLink,
        this.searchResultEntry.userManualLink,
        this.searchResultEntry.referenceManualLink,
        this.searchResultEntry.gettingStartedLink,
        this.searchResultEntry.specificationLink,
        this.searchResultEntry.regulatoryLink
      ];
    }
  }

  getLink(urlString: string | undefined): string | undefined {
    if (urlString) {
      try {
        const url = new URL(urlString);
        return url.pathname.endsWith('/search.html') ? undefined : urlString;
      } catch (e) {
      }
    }
    return urlString;
  }

  triggerSearch(urlString: string | undefined) {
    if (urlString) {
      const url = new URL(urlString);
      if (url.pathname.endsWith('/search.html')) {
        const params: any = {};
        url.searchParams.forEach((value, key) => {
          params[key] = value;
        });
        this.searchEventPublisher.publishEvent(SearchEventTypeEnum.SEARCH_FROM_URL, params as SearchParams);
      }
    }
  }
}
